// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components
import { BaseInput, BaseSelect, BaseLabel, Loading, BaseDatePicker, BaseInputAddress } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormAppointment, IPayloadEditAppointment } from '~/utils/interface/appointment';
import { schema, defaultValuesAppointmentForm } from './helper';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { createAppointment, detailAppointment, updateAppointment } from '~/thunks/appointment/appointmentThunk';
import { cleanEmptyFields, getChangedDataForm } from '~/utils/helper/common';
import { optionAppointmentStatus, optionAppointmentType } from '~/utils/constants/common';
import { clientActions } from '~/thunks/client/clientSlice';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './AppointmentForm.module.scss';

type Props = {
  appointmentId?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const AppointmentForm = (props: Props) => {
  //#region Destructuring Props
  const { appointmentId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const { id: clientId } = useParams();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<IFormAppointment>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesAppointmentForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!appointmentId) return;

    handleGetDetailAppointment(appointmentId);
  }, [appointmentId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailAppointment = (appointmentId: string) => {
    setIsLoading(true);

    dispatch(detailAppointment(appointmentId))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        const { id, ...newData } = data;

        reset(cleanEmptyFields(newData));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleCloseModal = () => {
    onClose?.();
    reset();
  };

  const handleUpdateAppointment = (data: IFormAppointment) => {
    if (!appointmentId) return;

    const cleanData = getChangedDataForm(dirtyFields, data) as IFormAppointment;

    const payload: IPayloadEditAppointment = {
      appointmentId,
      body: cleanData,
    };

    setIsLoading(true);
    dispatch(updateAppointment(payload))
      .unwrap()
      .then(() => {
        dispatch(clientActions.setRefreshClientDetail(true));
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleCreateAppointment = (data: IFormAppointment) => {
    if (!clientId) {
      return;
    }

    const cleanData = cleanEmptyFields({
      ...data,
      clientId,
    });

    setIsLoading(true);
    dispatch(createAppointment(cleanData))
      .unwrap()
      .then(() => {
        dispatch(clientActions.setRefreshClientDetail(true));
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const fetchOptionsType = async (): Promise<IResultCallApiFn<IBaseOption>> => ({
    optionsResp: optionAppointmentType,
    hasMoreResp: false,
  });

  const fetchOptionStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionAppointmentStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='appointmentFormComponent'
      aria-labelledby='AppointmentForm'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle id='AppointmentForm' className={cx('header')}>
        <Stack direction='column' spacing={1}>
          <BaseLabel
            label={t(!appointmentId ? 'form_appointment_title' : 'form_appointment_title_edit')}
            styleType='bigTitle'
          />
          <Typography className={cx('description')}>
            {t(!appointmentId ? 'form_appointment_description' : 'form_appointment_description_edit')}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Controller
              control={control}
              name='title'
              render={({ field }) => (
                <BaseInput
                  required
                  label={t('form_appointment_title_label')}
                  placeholder={t('form_appointment_title_placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors.title?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='type'
              render={({ field }) => (
                <BaseSelect
                  required
                  styleType='contained'
                  gap={8}
                  label={t('form_appointment_type_label')}
                  placeholder={t('form_appointment_type_placeholder')}
                  callApiFn={fetchOptionsType}
                  value={field.value}
                  onChange={(opt) => field.onChange(opt?.value)}
                  errorMessage={errors.type?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='date'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='date'
                  label={t('form_appointment_date_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.date?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='startTime'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='time'
                  label={t('form_appointment_start_time_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.startTime?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='endTime'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='time'
                  label={t('form_appointment_end_time_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.endTime?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='status'
              render={({ field }) => (
                <BaseSelect
                  required
                  styleType='contained'
                  gap={8}
                  label={t('form_appointment_status_label')}
                  placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
                  callApiFn={fetchOptionStatus}
                  value={field.value}
                  onChange={(opt) => field.onChange(opt?.value)}
                  errorMessage={errors.status?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='location'
              render={({ field }) => (
                <BaseInputAddress
                  showIcon
                  height={45}
                  placeholder={t('form_appointment_address_label')}
                  label={t('form_appointment_location_label')}
                  value={field.value}
                  onSelectAddress={(data) => field.onChange(data.address)}
                  errorMessage={errors.location?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name='note'
              render={({ field }) => (
                <BaseInput
                  multiline
                  height={'auto'}
                  rows={6}
                  label={t('form_appointment_note_label')}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors.note?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      {isLoading && <Loading />}

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          disabled={appointmentId ? Object.keys(dirtyFields).length === 0 : false}
          onClick={handleSubmit(!appointmentId ? handleCreateAppointment : handleUpdateAppointment)}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentForm;
